import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import UserLayout from "../components/UserLayout";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Table from "@mui/material/Table";
import {Pagination} from "@mui/lab";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import * as XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {robotoRegularBase64} from "../shared/pdf-fonts";
import {AppState} from "../store";
import {CreateNotaryDialog} from "./create-notary/createNotaryDialog";
import {getAllNotariesAction, getAllPaginatedNotariesAction} from "../api/settings";
import {AsyncTaskStatusType} from "../shared/dtos";

export default function Notary() {
    const [searchParams] = useSearchParams();
    const page = Number(searchParams.get("page") || "1");
    const filterByName = searchParams.get("filterByName") || "";
    const dispatch: any = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    const [createNotaryDialogOpen, setCreateNotaryDialogOpen] = useState(false);

    useEffect(() => {
        dispatch(getAllNotariesAction());
        dispatch(getAllPaginatedNotariesAction({filterByName, page}));
    }, [filterByName, page]);

    const notaries = useSelector((state: AppState) => state.settings.allPaginatedNotaries);
    const getAllPaginatedNotaryTaskStatus = useSelector((state: AppState) => state.settings.getAllPaginatedNotariesTaskStatus);
    const allPaginatedNotaries = useSelector((state: AppState) => state.settings.allPaginatedNotaries);

    const handleChangePage = (_: React.ChangeEvent<unknown>, value: number) => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set("page", value.toString());
        navigate(`?${searchParams.toString()}`);
    };

    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set("filterByName", event.target.value);
        searchParams.set("page", "1");
        navigate(`?${searchParams.toString()}`);
    };

    const handleCreateNotaryDialogOpen = () => setCreateNotaryDialogOpen(true);

    const handleCreateNotaryDialogClose = () => {
        setCreateNotaryDialogOpen(false);
        dispatch(getAllPaginatedNotariesAction({filterByName, page}));
    };

    const exportToExcel = () => {
        if (notaries?.entity) {
            const headers = [["Noter Adı", "Şehir"]];
            const worksheet = XLSX.utils.json_to_sheet(
                notaries.entity.map((notary: any) => ({
                    "Noter Adı": notary.name,
                    "Şehir": notary.city.name,
                }))
            );

            const workbook = XLSX.utils.book_new();
            XLSX.utils.sheet_add_aoa(worksheet, headers, {origin: "A1"});

            const headerRow = headers[0];
            const columnWidths = headerRow.map(header => ({wch: header.length + 5}));
            worksheet['!cols'] = columnWidths;
            headerRow.forEach((_, index) => {
                const cellRef = XLSX.utils.encode_cell({r: 0, c: index});
                if (worksheet[cellRef]) {
                    worksheet[cellRef].s = {
                        font: {bold: true},
                        fill: {
                            patternType: "solid",
                            fgColor: {rgb: "D3D3D3"}
                        }
                    };
                }
            });

            XLSX.utils.book_append_sheet(workbook, worksheet, "Noterler Listesi");
            XLSX.writeFile(workbook, "noterler_listesi.xlsx");
        }
    };

    const exportToPDF = () => {
        if (notaries?.entity) {
            const doc = new jsPDF();
            doc.addFileToVFS("Roboto-Regular.ttf", robotoRegularBase64);
            doc.addFont("Roboto-Regular.ttf", "Roboto", "normal");
            doc.setFont("Roboto");

            doc.setFontSize(18);
            doc.text("Noterler Listesi", 14, 20);

            const tableData = notaries.entity.map((notary: any) => [
                notary.name,
                notary.city.name,
            ]);

            const tableColumns = ["Noter Adı", "Şehir"];

            autoTable(doc, {
                head: [tableColumns],
                body: tableData,
                startY: 30,
                margin: {horizontal: 10},
                styles: {
                    font: "Roboto",
                    cellPadding: 3,
                    fontSize: 8,
                    valign: "middle",
                    halign: "left",
                },
            });

            doc.save("noterler_listesi.pdf");
        }
    };

    const handleCreate = () => {
        navigate("");
        dispatch(getAllPaginatedNotariesAction({filterByName, page}));
        handleCreateNotaryDialogClose();
    }

    return (
        <UserLayout>
            <div id="sidebar">
                <h1>NOTERLER</h1>
                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2}}>
                    <Fab
                        size="small"
                        color="primary"
                        aria-label="add"
                        sx={{marginLeft: "auto", marginBottom: 5}}
                        onClick={handleCreateNotaryDialogOpen}>
                        <AddIcon/>
                    </Fab>
                </Box>
                <Box sx={{display: "flex", gap: 2, mb: 2}}>
                    <Button variant="contained" color="primary" onClick={exportToExcel}>
                        Excel'e Dışa Aktar
                    </Button>
                    <Button variant="contained" color="secondary" onClick={exportToPDF}>
                        PDF'e Dışa Aktar
                    </Button>
                </Box>
            </div>

            {createNotaryDialogOpen && (
                <CreateNotaryDialog handleClose={handleCreateNotaryDialogClose}
                                    handleCreate={handleCreate}/>
            )}

            <Box sx={{overflowX: "auto"}}>
                <Table sx={{minWidth: 800}} aria-label="notaries table">
                    <TableHead>
                        <TableRow>
                            <TableCell>NOTER ADI</TableCell>
                            <TableCell>ŞEHİR</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {notaries?.entity.map((notary) => (
                            <TableRow key={notary.id}>
                                <TableCell>{notary.name}</TableCell>
                                <TableCell>{notary.city.name}</TableCell>
                            </TableRow>
                        ))}
                        {getAllPaginatedNotaryTaskStatus?.type !== AsyncTaskStatusType.Loading && allPaginatedNotaries?.numberOfElements === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} style={{textAlign: "center"}}>
                                    Noterler Bulunamadı
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Box>
            {notaries?.totalNumberPages != null && notaries.totalNumberPages > 1 && (
                <div style={{display: "flex", justifyContent: "center", marginTop: "10px"}}>
                    <Pagination
                        page={page}
                        count={notaries.totalNumberPages}
                        defaultPage={1}
                        onChange={handleChangePage}/>
                </div>
            )}
        </UserLayout>
    );
}
